import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  DirectionsCarFilled,
  Category,
  Store,
  Sell,
  AttachMoney,
  Dashboard as DashboardIcon,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  LocalShipping,
  Inbox,
  Business,
  LocalGasStation,
  Assignment,
  ArrowForward,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DashboardData } from './dashboardService';
import { useCompany } from '../../../contexts/CompanyContext';
import { useThemeMode } from '../../../theme/Thememodecontext';
import { useDashboardData, usePipelineStats } from '../../../hooks/useDashboard';

const PALETTE = {
  blue:   '#3b82f6',
  green:  '#10b981',
  amber:  '#f59e0b',
  red:    '#ef4444',
  teal:   '#5A9FB5',
  violet: '#8B7AA8',
  slate:  '#5B7C99',
  sage:   '#69A884',
  tan:    '#B8956A',
  mauve:  '#A67C8E',
  mist:   '#7A9B8E',
};

const STATUS_COLORS: Record<string, string> = {
  completed: PALETTE.green,
  confirmed: PALETTE.blue,
  pending:   PALETTE.amber,
  cancelled: PALETTE.red,
  delivered: '#059669',
};

const CHART_COLORS = [
  PALETTE.slate,
  PALETTE.violet,
  PALETTE.teal,
  PALETTE.sage,
  PALETTE.tan,
  PALETTE.mauve,
  PALETTE.mist,
];

const FUEL_TYPE_COLORS: Record<string, string> = {
  Petrol: PALETTE.amber,
  Diesel: PALETTE.slate,
  Hybrid: PALETTE.sage,
  Electric: PALETTE.green,
  'LPG/Gas': PALETTE.violet,
  Unspecified: PALETTE.mist,
};

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  baseColor: string;
  trend?: { value: number; isPositive: boolean };
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, baseColor, trend, subtitle }) => (
  <Card
    sx={{
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      background: `linear-gradient(135deg, ${baseColor} 0%, ${alpha(baseColor, 0.75)} 100%)`,
      color: 'white',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 18px ${alpha(baseColor, 0.4)}`,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -12, right: -12,
        width: 52, height: 52,
        borderRadius: '50%',
        background: alpha('#ffffff', 0.08),
        pointerEvents: 'none',
      },
    }}
  >
    <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: alpha('#ffffff', 0.85), mb: 0.3 }} noWrap>
            {title}
          </Typography>
          <Typography variant="h5" component="div" sx={{ fontWeight: 800, mb: 0.3, fontSize: '1.35rem', color: '#ffffff', lineHeight: 1.15 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.68rem', color: alpha('#ffffff', 0.75) }} noWrap>{subtitle}</Typography>
          )}
          {trend && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.6, px: 0.7, py: 0.15, borderRadius: 999, bgcolor: alpha('#ffffff', 0.15) }}>
              {trend.isPositive
                ? <TrendingUp sx={{ fontSize: 13, mr: 0.3 }} />
                : <TrendingDown sx={{ fontSize: 13, mr: 0.3 }} />}
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.64rem' }}>
                {trend.value}% vs last month
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{ bgcolor: alpha('#ffffff', 0.18), width: 34, height: 34, backdropFilter: 'blur(8px)', border: `1.5px solid ${alpha('#ffffff', 0.25)}` }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const NoDataDisplay: React.FC<{ message?: string }> = ({ message = 'No data available' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, py: 4 }}>
    <Inbox sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);

const SectionCard: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.05)', ...sx }}>
    {children}
  </Card>
);

const SectionTitle: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
    {icon}{children}
  </Typography>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { companyProfile } = useCompany();
  const { isDark, colors } = useThemeMode();

  const { data, isLoading, isError, error } = useDashboardData();
  const { data: pipeline } = usePipelineStats();

  const tooltipContentStyle = {
    borderRadius: 10,
    border: `1px solid ${colors.neutral.gray[200]}`,
    boxShadow: `0 4px 16px ${alpha('#000', isDark ? 0.35 : 0.12)}`,
    background: colors.background.paper,
    color: colors.neutral.gray[800],
  };

  const tooltipCursorStyle: React.SVGProps<SVGElement> = {
    fill: isDark
      ? alpha(colors.primary.main, 0.12)
      : alpha(PALETTE.slate, 0.06),
  };

  const formatPrice = (price: number) => {
    const currency = companyProfile?.currency || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} sx={{ color: PALETTE.slate }} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>Loading Dashboard…</Typography>
        </Box>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {(error as Error)?.message || 'Failed to load dashboard data'}
        </Alert>
      </Box>
    );
  }

  const revenueByStatusData = [
    { name: 'Completed', value: (data.revenueByStatus.completed || 0) + (data.revenueByStatus.delivered || 0), color: STATUS_COLORS.completed },
    { name: 'Confirmed', value: data.revenueByStatus.confirmed || 0, color: STATUS_COLORS.confirmed },
    { name: 'Pending',   value: data.revenueByStatus.pending   || 0, color: STATUS_COLORS.pending   },
  ].filter(item => item.value > 0);

  const orderStatusData = [
    { name: 'Completed', value: data.completedOrders || 0, color: STATUS_COLORS.completed },
    { name: 'Confirmed', value: data.confirmedOrders || 0, color: STATUS_COLORS.confirmed },
    { name: 'Pending',   value: data.pendingOrders   || 0, color: STATUS_COLORS.pending   },
    { name: 'Cancelled', value: data.cancelledOrders || 0, color: STATUS_COLORS.cancelled },
  ].filter(item => item.value > 0);

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: alpha(PALETTE.slate, 0.03),
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-track': { background: alpha(PALETTE.slate, 0.06) },
        '&::-webkit-scrollbar-thumb': { background: alpha(PALETTE.slate, 0.25), borderRadius: 4 },
        '&::-webkit-scrollbar-thumb:hover': { background: alpha(PALETTE.slate, 0.45) },
      }}
    >
      <Box sx={{ p: 3 }}>

        {/* ── Header ── */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">Dashboard</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                Real-time dealership metrics — inventory, imports, and sales
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ── Key metric cards ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Vehicles" value={data.totalProducts}
              icon={<DirectionsCarFilled sx={{ fontSize: 18 }} />} baseColor={PALETTE.slate}
              subtitle={`${data.activeProducts} in stock • ${data.featuredProducts} featured`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Body Types" value={data.totalCategories}
              icon={<Category sx={{ fontSize: 18 }} />} baseColor={PALETTE.violet}
              subtitle={`${data.activeCategories} active`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Branches" value={data.totalWarehouses}
              icon={<Store sx={{ fontSize: 18 }} />} baseColor={PALETTE.teal}
              subtitle={`${data.activeWarehouses} active`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Sales" value={data.totalOrders}
              icon={<Sell sx={{ fontSize: 18 }} />} baseColor={PALETTE.sage}
              subtitle={`${data.pendingOrders} pending`}
            />
          </Grid>
        </Grid>

        {/* ── Revenue overview + Stock alerts ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <SectionCard>
              <CardContent>
                <SectionTitle icon={<AttachMoney sx={{ color: PALETTE.green }} />}>Revenue Overview</SectionTitle>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                  <Box
                    sx={{
                      flex: { lg: '0 0 auto' }, width: { lg: '45%' }, p: 3, borderRadius: 2.5,
                      background: `linear-gradient(135deg, ${alpha(PALETTE.green, 0.08)} 0%, ${alpha(PALETTE.teal, 0.06)} 100%)`,
                      border: `1.5px solid ${alpha(PALETTE.green, 0.2)}`, display: 'flex', flexDirection: 'column',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>Total Revenue</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: PALETTE.green, mb: 2, fontSize: { xs: '1.75rem', sm: '2.125rem' }, wordBreak: 'break-word' }}>
                      {formatPrice(data.totalRevenue)}
                    </Typography>
                    <Divider sx={{ my: 1.5, borderColor: alpha(PALETTE.green, 0.15) }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Average Sale Value</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: alpha(PALETTE.green, 0.85) }}>
                      {formatPrice(data.averageOrderValue)}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                      Revenue by Sale Status
                    </Typography>
                    {revenueByStatusData.length > 0 ? (
                      <Box sx={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={revenueByStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                              {revenueByStatusData.map((entry, index) => (
                                <Cell key={`rev-cell-${index}`} fill={entry.color} fillOpacity={0.85 - index * 0.08} stroke={entry.color} strokeOpacity={0.4} strokeWidth={1} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: any) => formatPrice(value)} contentStyle={tooltipContentStyle} itemStyle={{ color: colors.neutral.gray[800] }} labelStyle={{ color: colors.neutral.gray[800] }} />
                            <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ paddingTop: 8, fontSize: 13, color: colors.neutral.gray[600] }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <NoDataDisplay message="No revenue data available" />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <SectionCard>
              <CardContent>
                <SectionTitle icon={<Inbox sx={{ color: PALETTE.teal }} />}>Sales Pipeline</SectionTitle>
                <Stack spacing={2}>
                  <Box
                    onClick={() => navigate('/admin/reservations')}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      background: `linear-gradient(120deg, ${alpha(PALETTE.blue, 0.1)} 0%, ${alpha(PALETTE.blue, 0.05)} 100%)`,
                      border: `1px solid ${alpha(PALETTE.blue, 0.25)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'translateX(2px)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={600}>New Inquiries</Typography>
                      <Chip label={pipeline?.newInquiries ?? '—'} size="small" sx={{ bgcolor: alpha(PALETTE.blue, 0.18), color: PALETTE.blue, border: `1px solid ${alpha(PALETTE.blue, 0.35)}`, fontWeight: 800, fontSize: '0.8rem' }} />
                    </Box>
                  </Box>
                  <Box
                    onClick={() => navigate('/admin/reservations')}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      background: `linear-gradient(120deg, ${alpha(PALETTE.amber, 0.1)} 0%, ${alpha(PALETTE.amber, 0.05)} 100%)`,
                      border: `1px solid ${alpha(PALETTE.amber, 0.28)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'translateX(2px)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={600}>Reserved (Deposit Paid)</Typography>
                      <Chip label={pipeline?.reserved ?? '—'} size="small" sx={{ bgcolor: alpha(PALETTE.amber, 0.18), color: PALETTE.amber, border: `1px solid ${alpha(PALETTE.amber, 0.35)}`, fontWeight: 800, fontSize: '0.8rem' }} />
                    </Box>
                  </Box>
                  <Divider sx={{ borderColor: alpha(PALETTE.slate, 0.1) }} />
                  <Box
                    onClick={() => navigate('/admin/trade-ins')}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      background: `linear-gradient(120deg, ${alpha(PALETTE.violet, 0.08)} 0%, ${alpha(PALETTE.violet, 0.05)} 100%)`,
                      border: `1px solid ${alpha(PALETTE.violet, 0.2)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'translateX(2px)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Trade-Ins Awaiting Review</Typography>
                      <Assignment sx={{ fontSize: 18, color: PALETTE.violet }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: PALETTE.violet }}>{pipeline?.pendingTradeIns ?? '—'}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: alpha(PALETTE.violet, 0.8) }}>
                        <Typography variant="caption">View submissions</Typography>
                        <ArrowForward sx={{ fontSize: 12 }} />
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Order trends + Status distribution ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={8}>
            <SectionCard>
              <CardContent>
                <SectionTitle>Sales Trends (Last 7 Days)</SectionTitle>
                {data.orderTrends && data.orderTrends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data.orderTrends}>
                      <defs>
                        <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={PALETTE.blue}  stopOpacity={0.55} />
                          <stop offset="95%" stopColor={PALETTE.blue}  stopOpacity={0.04} />
                        </linearGradient>
                        <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={PALETTE.green} stopOpacity={0.55} />
                          <stop offset="95%" stopColor={PALETTE.green} stopOpacity={0.04} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(PALETTE.slate, isDark ? 0.2 : 0.12)} />
                      <XAxis dataKey="date" stroke={alpha(PALETTE.slate, 0.4)} tick={{ fontSize: 12, fill: colors.neutral.gray[500] }} tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                      <YAxis yAxisId="orders" orientation="left" stroke={alpha(PALETTE.blue, 0.5)} tick={{ fontSize: 12, fill: PALETTE.blue }} allowDecimals={false} tickCount={6} label={{ value: 'Sales', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11, fill: alpha(PALETTE.blue, 0.8) } }} />
                      <YAxis yAxisId="revenue" orientation="right" stroke={alpha(PALETTE.green, 0.5)} tick={{ fontSize: 12, fill: PALETTE.green }}
                        tickFormatter={(v) => {
                          if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                          if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}K`;
                          return v;
                        }}
                        label={{ value: 'Revenue', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11, fill: alpha(PALETTE.green, 0.8) } }}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        itemStyle={{ color: colors.neutral.gray[800] }}
                        labelStyle={{ color: colors.neutral.gray[600], fontWeight: 600 }}
                        cursor={tooltipCursorStyle}
                        labelFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        formatter={(value: any, name: string) => name === 'revenue' ? [formatPrice(value), 'Revenue'] : [value, 'Sales']}
                      />
                      <Legend wrapperStyle={{ color: colors.neutral.gray[600] }} />
                      <Area yAxisId="orders" type="monotone" dataKey="orders" stroke={PALETTE.blue} strokeWidth={2.5} fillOpacity={1} fill="url(#gradOrders)" dot={{ r: 4, fill: PALETTE.blue, fillOpacity: 0.7, strokeWidth: 0 }} activeDot={{ r: 6, fill: PALETTE.blue }} />
                      <Area yAxisId="revenue" type="monotone" dataKey="revenue" stroke={PALETTE.green} strokeWidth={2.5} fillOpacity={1} fill="url(#gradRevenue)" dot={{ r: 4, fill: PALETTE.green, fillOpacity: 0.7, strokeWidth: 0 }} activeDot={{ r: 6, fill: PALETTE.green }} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <NoDataDisplay message="No order trends data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SectionCard>
              <CardContent>
                <SectionTitle>Sale Status Distribution</SectionTitle>
                {orderStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={orderStatusData} cx="50%" cy="50%" labelLine={false} outerRadius={100} dataKey="value" label={false}>
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`status-cell-${index}`} fill={entry.color} fillOpacity={0.82 - index * 0.06} stroke={entry.color} strokeOpacity={0.35} strokeWidth={1} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipContentStyle} itemStyle={{ color: colors.neutral.gray[800] }} labelStyle={{ color: colors.neutral.gray[800] }} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: colors.neutral.gray[600] }} formatter={(value, entry: any) => `${value}: ${entry.payload.value}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <NoDataDisplay message="No order status data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Vehicles by body type + Best-selling vehicles ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={6}>
            <SectionCard>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionTitle>Vehicles by Body Type</SectionTitle>
                {data.productsByCategory && data.productsByCategory.length > 0 ? (
                  <Box sx={{ flex: 1, minHeight: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.productsByCategory.slice(0, 6)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(PALETTE.slate, isDark ? 0.2 : 0.1)} />
                        <XAxis type="number" stroke={alpha(PALETTE.slate, 0.35)} tick={{ fontSize: 12, fill: colors.neutral.gray[500] }} />
                        <YAxis dataKey="categoryName" type="category" width={120} stroke={alpha(PALETTE.slate, 0.35)} tick={{ fontSize: 12, fill: colors.neutral.gray[600] }} />
                        <Tooltip contentStyle={tooltipContentStyle} itemStyle={{ color: colors.neutral.gray[800] }} labelStyle={{ color: colors.neutral.gray[600], fontWeight: 600 }} cursor={tooltipCursorStyle} />
                        <Bar dataKey="productCount" radius={[0, 8, 8, 0]}>
                          {data.productsByCategory.slice(0, 6).map((entry, index) => (
                            <Cell key={`bar-cell-${index}`} fill={PALETTE.slate} fillOpacity={0.85 - index * 0.1} stroke={PALETTE.teal} strokeOpacity={0.3} strokeWidth={1} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <NoDataDisplay message="No category data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionTitle>Best-Selling Vehicles</SectionTitle>
                {data.topProducts && data.topProducts.length > 0 ? (
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    {data.topProducts.slice(0, 5).map((product, index) => {
                      const base = CHART_COLORS[index % CHART_COLORS.length];
                      return (
                        <Box
                          key={product.productId}
                          sx={{
                            display: 'flex', alignItems: 'center', p: 1.75, borderRadius: 2,
                            background: `linear-gradient(120deg, ${alpha(base, 0.09)} 0%, ${alpha(base, 0.04)} 100%)`,
                            border: `1px solid ${alpha(base, 0.2)}`,
                            transition: 'all 0.2s ease',
                            '&:hover': { transform: 'translateX(4px)', boxShadow: `0 4px 14px ${alpha(base, 0.2)}`, border: `1px solid ${alpha(base, 0.35)}` },
                          }}
                        >
                          <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: alpha(base, 0.18), border: `1.5px solid ${alpha(base, 0.35)}`, color: base, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem', mr: 1.75, flexShrink: 0 }}>
                            #{index + 1}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: 'text.primary' }}>{product.productName}</Typography>
                            <Typography variant="caption" sx={{ color: alpha(base, 0.8), fontWeight: 500 }}>Revenue: {formatPrice(product.revenue)}</Typography>
                          </Box>
                          <Chip label={`${product.totalSold} sold`} size="small" sx={{ fontWeight: 700, bgcolor: alpha(base, 0.15), color: base, border: `1px solid ${alpha(base, 0.3)}`, ml: 1, flexShrink: 0, fontSize: '0.72rem' }} />
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <NoDataDisplay message="No top products data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Vehicles by brand + fuel type ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={6}>
            <SectionCard>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionTitle icon={<Business sx={{ color: PALETTE.violet }} />}>Vehicles by Brand</SectionTitle>
                {data.productsByBrand && data.productsByBrand.length > 0 ? (
                  <Box sx={{ flex: 1, minHeight: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.productsByBrand.slice(0, 6)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(PALETTE.slate, isDark ? 0.2 : 0.1)} />
                        <XAxis type="number" stroke={alpha(PALETTE.slate, 0.35)} tick={{ fontSize: 12, fill: colors.neutral.gray[500] }} allowDecimals={false} />
                        <YAxis dataKey="brandName" type="category" width={120} stroke={alpha(PALETTE.slate, 0.35)} tick={{ fontSize: 12, fill: colors.neutral.gray[600] }} />
                        <Tooltip contentStyle={tooltipContentStyle} itemStyle={{ color: colors.neutral.gray[800] }} labelStyle={{ color: colors.neutral.gray[600], fontWeight: 600 }} cursor={tooltipCursorStyle} formatter={(value: any) => [`${value} vehicles`, 'In Stock']} />
                        <Bar dataKey="vehicleCount" radius={[0, 8, 8, 0]}>
                          {data.productsByBrand.slice(0, 6).map((entry, index) => (
                            <Cell key={`brand-cell-${index}`} fill={PALETTE.violet} fillOpacity={0.85 - index * 0.1} stroke={PALETTE.mauve} strokeOpacity={0.3} strokeWidth={1} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <NoDataDisplay message="No brand data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionTitle icon={<LocalGasStation sx={{ color: PALETTE.amber }} />}>Vehicles by Fuel Type</SectionTitle>
                {data.productsByFuelType && data.productsByFuelType.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={data.productsByFuelType}
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={100}
                        paddingAngle={4}
                        dataKey="vehicleCount"
                        nameKey="fuelType"
                      >
                        {data.productsByFuelType.map((entry, index) => (
                          <Cell
                            key={`fuel-cell-${index}`}
                            fill={FUEL_TYPE_COLORS[entry.fuelType] || CHART_COLORS[index % CHART_COLORS.length]}
                            fillOpacity={0.85}
                            stroke={FUEL_TYPE_COLORS[entry.fuelType] || CHART_COLORS[index % CHART_COLORS.length]}
                            strokeOpacity={0.4}
                            strokeWidth={1}
                          />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipContentStyle} itemStyle={{ color: colors.neutral.gray[800] }} labelStyle={{ color: colors.neutral.gray[800] }} formatter={(value: any) => [`${value} vehicles`, '']} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: colors.neutral.gray[600] }} formatter={(value, entry: any) => `${value}: ${entry.payload.vehicleCount}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <NoDataDisplay message="No fuel type data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Branch capacity ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <SectionCard>
              <CardContent>
                <SectionTitle icon={<Store sx={{ color: PALETTE.teal }} />}>Branch Inventory Capacity</SectionTitle>
                {data.warehouses && data.warehouses.length > 0 ? (
                  <Grid container spacing={3}>
                    {data.warehouses.map((warehouse) => {
                      const pct = warehouse.utilizationPercentage;
                      const utilColor = pct > 90 ? PALETTE.red : pct > 70 ? PALETTE.amber : PALETTE.green;
                      return (
                        <Grid item xs={12} md={6} lg={4} key={warehouse.id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2.5, borderRadius: 2.5,
                              background: `linear-gradient(135deg, ${alpha(utilColor, 0.06)} 0%, ${alpha(PALETTE.slate, 0.03)} 100%)`,
                              border: `1px solid ${alpha(utilColor, 0.18)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': { boxShadow: `0 8px 24px ${alpha(utilColor, 0.18)}`, transform: 'translateY(-2px)', border: `1px solid ${alpha(utilColor, 0.32)}` },
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="h6" fontWeight={700} noWrap>{warehouse.name}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{warehouse.code} • {warehouse.productCount} vehicles</Typography>
                              </Box>
                              <Chip
                                label={warehouse.isActive ? 'Active' : 'Inactive'} size="small"
                                sx={{ bgcolor: warehouse.isActive ? alpha(PALETTE.green, 0.15) : alpha(PALETTE.slate, 0.1), color: warehouse.isActive ? PALETTE.green : 'text.secondary', border: `1px solid ${warehouse.isActive ? alpha(PALETTE.green, 0.3) : alpha(PALETTE.slate, 0.2)}`, fontWeight: 700, flexShrink: 0 }}
                              />
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>Capacity Usage</Typography>
                                <Typography variant="body2" fontWeight={800} sx={{ color: utilColor }}>{pct}%</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, bgcolor: alpha(utilColor, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 4, background: `linear-gradient(90deg, ${utilColor} 0%, ${alpha(utilColor, 0.7)} 100%)` } }} />
                            </Box>
                            <Grid container spacing={1}>
                              {[
                                { label: 'Total',     value: warehouse.total,     color: PALETTE.slate },
                                { label: 'Used',      value: warehouse.used,      color: utilColor     },
                                { label: 'Available', value: warehouse.available, color: PALETTE.green },
                              ].map(({ label, value, color }) => (
                                <Grid item xs={4} key={label}>
                                  <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: alpha(color, 0.07), border: `1px solid ${alpha(color, 0.14)}` }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>{label}</Typography>
                                    <Typography variant="h6" fontWeight={800} sx={{ color, fontSize: '1.05rem' }}>{value}</Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <NoDataDisplay message="No show room data available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Recent orders ── */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionCard>
              <CardContent>
                <SectionTitle>Recent Sales</SectionTitle>
                {data.recentOrders && data.recentOrders.length > 0 ? (
                  <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(PALETTE.slate, 0.1)}`, overflow: 'hidden' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: `linear-gradient(90deg, ${alpha(PALETTE.slate, 0.07)} 0%, ${alpha(PALETTE.teal, 0.05)} 100%)` }}>
                          {['Sale No.', 'Customer', 'Units', 'Total', 'Status', 'Payment', 'Date'].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 700, color: alpha(PALETTE.slate, 0.85), borderBottom: `2px solid ${alpha(PALETTE.slate, 0.1)}`, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.recentOrders.map((order, rowIdx) => (
                          <TableRow
                            key={order.id}
                            hover
                            sx={{
                              bgcolor: rowIdx % 2 === 0 ? 'transparent' : alpha(PALETTE.slate, 0.018),
                              '&:hover': { bgcolor: alpha(PALETTE.teal, 0.05) },
                              '& td': { borderBottom: `1px solid ${alpha(PALETTE.slate, 0.07)}` },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} sx={{ color: PALETTE.teal, fontFamily: 'monospace', letterSpacing: '0.3px' }}>
                                {order.orderNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{order.customerName}</Typography>
                              <Typography variant="caption" sx={{ color: alpha(PALETTE.slate, 0.55) }}>{order.customerEmail}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={order.itemCount} size="small" sx={{ bgcolor: alpha(PALETTE.slate, 0.08), color: PALETTE.slate, border: `1px solid ${alpha(PALETTE.slate, 0.2)}`, fontWeight: 700, minWidth: 36 }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} sx={{ color: PALETTE.green }}>{formatPrice(order.totalAmount)}</Typography>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const key = order.status.toLowerCase();
                                const col = STATUS_COLORS[key] || STATUS_COLORS.pending;
                                const Icon =
                                  key === 'completed' || key === 'delivered' ? CheckCircle :
                                  key === 'cancelled'                        ? Cancel :
                                  key === 'confirmed'                        ? LocalShipping : HourglassEmpty;
                                return (
                                  <Chip
                                    label={order.status} size="small"
                                    icon={<Icon sx={{ fontSize: '14px !important', color: `${col} !important` }} />}
                                    sx={{ bgcolor: alpha(col, 0.12), color: col, border: `1px solid ${alpha(col, 0.28)}`, fontWeight: 600, textTransform: 'capitalize' }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const col = order.paymentStatus === 'paid' ? PALETTE.green : order.paymentStatus === 'pending' ? PALETTE.amber : PALETTE.red;
                                return (
                                  <Chip label={order.paymentStatus} size="small" sx={{ bgcolor: alpha(col, 0.1), color: col, border: `1px solid ${alpha(col, 0.28)}`, fontWeight: 600, textTransform: 'capitalize' }} />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ color: alpha(PALETTE.slate, 0.6), fontWeight: 500 }}>
                                {order.orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <NoDataDisplay message="No recent orders available" />
                )}
              </CardContent>
            </SectionCard>
          </Grid>
        </Grid>

      </Box>
    </Box>
  );
};

export default Dashboard;